import type { TLocalized } from '@hubertai/screening-question-types';
import { COLOR_MAP } from '../types/constants';
import type { QuestionOption } from '../types/templateWithSuggestedQuestions.types';

export function getBlockColors(index: number) {
  return COLOR_MAP[Object.keys(COLOR_MAP)[index % Object.keys(COLOR_MAP).length]];
}
export function resolveOptionLabel(option: QuestionOption, lang: string): string {
  const labelArr = option.label[lang as keyof TLocalized<string[]>] ?? option.label['en-gb'] ?? [];
  const label = Array.isArray(labelArr) ? labelArr[0] : labelArr;
  return label || '—';
}
/**
 * Whether the interview still offers this option in `lang`.
 *
 * Only an explicit `false` turns an option off, matching the question editor's own reading of the
 * flag (TypeSpecificEditor's `option.enabled?.[questionLanguage] !== false`): an option the editor
 * has never touched for a language carries no entry at all and is still asked.
 */
export function isOptionEnabled(option: QuestionOption, lang: string): boolean {
  return option.enabled?.[lang as keyof TLocalized<boolean>] !== false;
}
// Only line-breaking tags separate words; inline ones (`<span>`, `<strong>`, …) sit flush against the
// text around them, so `rekrytering</span>?` stays "rekrytering?" as the template editor shows it.
const BLOCK_TAG = /<\/?(?:br|p|div|li|ul|ol|h[1-6]|blockquote|tr|td|th)\b[^>]*>/gi;

export function stripHtml(html: string): string {
  return html
    .replace(/&nbsp;|&#160;|&#xA0;/gi, ' ')
    .replace(BLOCK_TAG, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}
// The template editor renders a question's text pieces back to back (QuestionV4's QuestionText), so
// they are joined with no separator here too.
export function resolveText(field: Partial<Record<string, string[]>> | undefined, lang: string): string {
  if (!field) return '';
  const arr = field[lang] ?? field['en-gb'] ?? field['en-us'] ?? [];
  return stripHtml(arr.join(''));
}
export function resolveLocalized<T>(field: Partial<Record<string, T>> | undefined, lang: string): T | undefined {
  return field?.[lang] ?? field?.['en-gb'] ?? field?.['en-us'];
}

// Node ids carry a `|<suffix>` when a question is duplicated into the template — strip it to
// look the shared question content up in the questions collection.
export function normalizeQuestionId(id: string): string {
  return id.split('|')[0]?.trim() ?? '';
}
